import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { first, Subject, takeUntil } from 'rxjs';
import { INivelesPromo, IProductos, IPromosEcommerce, T_TipoPromocion } from '../../../private/shared/models/productos';
import { TransferDataService } from '../../../private/shared/services/transfer-data.service';
import { addCartPromoState } from '../../../store/actions/ecommerce.actions';
import { AppState } from '../../../store/app-reducers';
import { CartProductsState } from '../../../store/reducers';
import { EcommerceAnalyticsService } from '../../analytics/ecommerce-analytics/ecommerce-analytics.service';
import { SharedAnalyticsService } from '../../analytics/shared-analytics/shared-analytics.service';
import { EAnalyticsPageViews } from '../../enums/analytics.enum';
import { IAnalyticsListContext } from '../../models/Analytics';
import { SECTIONS } from '../../models/Constants';
import { ModalsService } from '../../services/modals/modals.service';
import { OperationsService } from '../../services/operations/operations.service';
import { PromoInfoModalPage } from '../promo-info-modal/promo-info-modal.page';
import { PromoSummaryModalPage } from '../promo-summary-modal/promo-summary-modal.page';

@Component({
  selector: 'getm-dynamic-promo',
  templateUrl: './dynamic-promo.page.html',
  styleUrls: ['./dynamic-promo.page.scss'],
})
export class DynamicPromoPage implements OnInit, OnDestroy {

  @Input() promo: IPromosEcommerce = {} as IPromosEcommerce;
  @Input() analyticsContext?: IAnalyticsListContext;

  private finishSubscriptions: Subject<boolean> = new Subject();
  private previousCartPromoProducts: IProductos[] = []; // Para detectar cambios en composición de productos

  public productosPromocion: IProductos[] = [];
  public disabledDinamico: boolean = false;
  public isLoading = true;
  public nivelesPromo: INivelesPromo[] = [];
  public level = 1;
  public promosEcommerce: IPromosEcommerce[] = [];
  public cartPromoProducts: IProductos[] = [];
  public isEditing = false;
  public isEditingLocked = false;
  public canConfirm = false;
  public isLastLevel = false;
  public showSummary = false;
  public promoAvailable = { PaquetesCanjeados: 0, MaximoPaquetes: 0 };
  public promoDetail = {
    title: '',
    available: 0,
    ends: '',
    select: 0,
    max: 0,
    tipoPromo: 'DINAMICO' as T_TipoPromocion,
  };

  constructor(
    private store: Store<AppState>
    , private modalS: ModalsService
    , private modalCtrl: ModalController
    , private transferDataS: TransferDataService
    , private operationsS: OperationsService
    , private ecommerceAnalyticsS: EcommerceAnalyticsService
    , private sharedAnalyticsS: SharedAnalyticsService
  ) { }

  ngOnInit() {
    // Disparar evento page_view para el modal de promoción dinámica
    this.sharedAnalyticsS.trackPageViewModal(EAnalyticsPageViews.promo_dinamica_modal, this.analyticsContext?.section || SECTIONS.TIENDA);

    if (!this.promo) {
      this.initializePromoDetail();
      this.isLoading = false;
      return;
    }

    this.nivelesPromo = this.promo.Niveles || [];
    this.cartPromoProducts = (this.promo.Productos || []).map(p => ({ ...p, productos: 0, Nivel: p.Nivel }));
    this.productosPromocion = this.promo?.Productos?.filter(p => p.Nivel === this.level) || [];

    this.operationsS.combinerCartOrder$().pipe(
      first(),
      takeUntil(this.finishSubscriptions)
    ).subscribe(this.callBackInfoPromo);
  }

  ngOnDestroy() {
    this.finishSubscriptions.next(true);
    this.finishSubscriptions.complete();
  }

  private callBackInfoPromo = (cart: CartProductsState) => {
    const cartPromos = cart.cartPromos.filter(p => p.ClavePromo === this.promo.ClavePromo);
    const exchange = cart.promo.find(p => p.ClavePromo === this.promo.ClavePromo)?.PaquetesCanjeados || 0;
    this.promoAvailable = {
      PaquetesCanjeados: exchange,
      MaximoPaquetes: this.promo.MaximoPaquetes || 0
    };
    if (cartPromos.length > 0) {
      this.cartPromoProducts = this.cartPromoProducts.map(pp => {
        const found = cartPromos.find(x => x.clave_producto === pp.clave_producto && x.Nivel === pp.Nivel && x.ClavePromo === this.promo.ClavePromo);
        return found ? { ...pp, productos: (found.productos || 0) / exchange, Nivel: found.Nivel } : pp;
      });
      // Activar modo edición cuando la promo ya está en el carrito
      this.isEditing = true;
      this.isEditingLocked = false;
      this.level = 1; // Nivel 1 activo
    }

    // Guardar el estado anterior de los productos para detectar cambios
    this.previousCartPromoProducts = JSON.parse(JSON.stringify(this.cartPromoProducts));

    const totalSelected = this.transferDataS.sumTotal(this.cartPromoProducts, 'productos');
    const activeNivel = this.nivelesPromo.find(n => totalSelected >= n.Minimo && totalSelected <= n.Maximo);
    this.level = activeNivel?.Nivel ?? 1;
    this.initializePromoDetail();
    this.updateState();
    this.isLoading = false;
  }

  public onQuantityChange(updatedProducts: IProductos[]) {
    // Actualizar cartPromoProducts con las cantidades del nivel activo
    this.cartPromoProducts = this.cartPromoProducts.map(p => {
      if (p.Nivel !== this.level) return p;
      const updated = updatedProducts.find(
        u => u.clave_producto === p.clave_producto && u.ClavePromo === p.ClavePromo && u.Nivel === p.Nivel
      );
      if (!updated) return p;

      return {
        ...updated,
        Maximo: p.Maximo, // preservar Maximo original
      };
    });

    this.updateState();
  }

  /** Cierra el modal sin confirmar */
  public onBack = () => {
    this.modalCtrl.dismiss(false);
  }

  /** Abre el modal de información de la promo */
  public onInfo = async () => {
    if (!this.promo) return;
    await this.modalS.presentModalPromoInfo(PromoInfoModalPage, { promo: this.promo });
  }

  /** Cambia el nivel activo desde el stepper del header */
  public onLevelClick = (nivel: number) => {
    this.level = nivel;
    if (this.isEditing) this.isEditingLocked = true;
    this.updateState();
  }

  /** Abre el resumen de la promo */
  public onSummary = async () => {
    if (!this.promo) return;
    await this.openPromoSummary(true);
  }

  /**
   * Avanza al siguiente nivel si no es el último,
   * o abre el resumen para confirmar si es el último nivel.
   */
  public onAction = async () => {
    if (!this.isLastLevel) {
      this.level = this.level + 1;
      if (this.isEditing) this.isEditingLocked = true;
      this.updateState();
    } else {
      this.isEditing = false;
      this.isEditingLocked = false;
      await this.openPromoSummary(false);
    }
  }

  /** Abre el resumen de la promo para confirmar o solo lectura */
  private openPromoSummary = async (isResume: boolean) => {
    const data = await this.modalS.presentModalPromoSummary(PromoSummaryModalPage, {
      promo: this.promo,
      productosPromocion: this.cartPromoProducts,
      isResume,
      quantity: this.promoAvailable.PaquetesCanjeados || 1,
    });

    if (typeof data === 'number') {
      await this.confirmPromoWithQuantity(data);
    } else if (data === true) {
      this.isEditing = true;
      this.isEditingLocked = false;
    }
  }

  /** Actualiza el estado completo del componente */
  private updateState() {
    if (!this.promo) return;

    // Calcular total seleccionado en el nivel activo
    const totalSelected = this.cartPromoProducts
      .filter(p => p.Nivel === this.level)
      .reduce((acc, p) => acc + (p.productos || 0), 0);

    // Actualizar nivelesPromo: Minimo y Cumple del nivel activo
    this.updateLevels(totalSelected);

    // Calcular currentLevel DESPUÉS de updateLevels para usar Cumple actualizado
    const currentLevel = this.nivelesPromo.find(n => n.Nivel === this.level);

    // Actualizar promoDetail.select y max
    this.updatePromoDetail(totalSelected, currentLevel);

    // Actualizar productosPromocion con Maximo dinámico (level max)
    this.updateProductosPromocion(totalSelected, currentLevel);

    // Calcular disabledDinamico
    this.disabledDinamico = this.cumpleNivel(currentLevel)

    // Calcular canConfirm: todos los niveles del nivel activo cumplen
    this.canConfirm = this.cumpleNivel(currentLevel)

    // Calcular isLastLevel: el nivel activo es el último nivel de la promo
    this.isLastLevel = this.level >= this.nivelesPromo.length;

    // Calcular showSummary: hay al menos un producto seleccionado en cualquier nivel
    this.showSummary = this.transferDataS.sumTotal(this.cartPromoProducts, 'productos') > 0;
  }

  /** Agrega la promo al store desde el modal de resumen con la cantidad especificada */
  private confirmPromoWithQuantity = async (quantity: number) => {
    const previousQuantity = this.promoAvailable.PaquetesCanjeados || 0;

    this.store.dispatch(addCartPromoState({
      promo: this.promo,
      cartPromos: this.cartPromoProducts,
      promoSelect: quantity,
    }));

    const addedItems: IProductos[] = [];
    const removedItems: IProductos[] = [];

    this.cartPromoProducts.forEach(current => {
      const previous = this.previousCartPromoProducts.find(
        p => p.clave_producto === current.clave_producto && p.Nivel === current.Nivel
      );
      const previousQty = previous ? (previous.productos || 0) * previousQuantity : 0;
      const currentQty = (current.productos || 0) * quantity;
      const delta = currentQty - previousQty;

      if (delta > 0) {
        addedItems.push({ ...current, productos: delta });
      } else if (delta < 0) {
        removedItems.push({ ...current, productos: Math.abs(delta) });
      }
    });

    const { listId, listName, section } = this.ecommerceAnalyticsS.resolvePromoAnalyticsContext(
      this.analyticsContext,
      'DINAMICO'
    );

    if (addedItems.length > 0) {
      this.ecommerceAnalyticsS.sendAddToCart({
        products: addedItems,
        value: (addedItems.reduce((sum, p) => sum + ((p.precio || 0) * (p.productos ?? 0)), 0)),
        section,
        listId,
        listName,
        promoMap: this.ecommerceAnalyticsS.buildPromoImpactMap([this.promo])
      });
    }

    if (removedItems.length > 0) {
      this.ecommerceAnalyticsS.sendRemoveFromCart({
        products: removedItems,
        value: (removedItems.reduce((sum, p) => sum + ((p.precio || 0) * (p.productos ?? 0)), 0)),
        section,
        listId,
        listName,
        promoMap: this.ecommerceAnalyticsS.buildPromoImpactMap([this.promo])
      });
    }

    this.modalCtrl.dismiss(true);
  }

  /** Inicializa promoDetail */
  private initializePromoDetail() {
    const available = this.promo?.MaximoPaquetes - this.promo?.PaquetesCanjeados || 0;
    this.promoDetail = {
      title: this.promo?.Promocion || '',
      available: available,
      ends: this.promo?.Vigencia?.toString() || '',
      select: 0,
      max: this.promo?.MaximoPaquetes || 0,
      tipoPromo: 'DINAMICO',
    };
  }

  private updateLevels(totalSelected: number): void {
    this.nivelesPromo = this.nivelesPromo.map(n => {
      const totalInLevel = this.cartPromoProducts
        .filter(p => p.Nivel === n.Nivel)
        .reduce((acc, p) => acc + (p.productos || 0), 0);

      return {
        ...n,
        Minimo: n.Nivel === this.level ? totalSelected : n.Minimo,
        Cumple: totalInLevel >= n.Maximo,
      };
    });
  }

  private updatePromoDetail(totalSelected: number, currentLevel: INivelesPromo | undefined): void {
    this.promoDetail = {
      ...this.promoDetail,
      select: totalSelected,
      max: currentLevel?.Maximo ?? 0,
    };
  }

  /** Actualiza productosPromocion con Maximo dinámico */
  private updateProductosPromocion(totalSelected: number, currentLevel: INivelesPromo | undefined): void {
    const levelProducts = this.cartPromoProducts.filter(p => p.Nivel === this.level);
    const levelMax = currentLevel?.Maximo ?? 0;

    this.productosPromocion = levelProducts.map(p => {
      const otherTotal = totalSelected - (p.productos || 0);
      const dynamicMax = levelMax > 0 ? Math.max(0, levelMax - otherTotal) : p.Maximo;
      return { ...p, Maximo: dynamicMax };
    });
  }

  private cumpleNivel(currentLevel: INivelesPromo | undefined): boolean {
    return currentLevel ? (currentLevel.Cumple || false) : false;
  }
}
